// ============================================================================
// src/app/navigation/routeElements.ts -- router-neutral legacy route elements.
// ============================================================================
// The seven legacy entry modules express their ownership as <AppRoutes> /
// <AppRoute> trees.  During B2 those trees must keep rendering under the
// current BrowserRouter, but they also need to be usable by the future
// TanStack-owned compatibility adapter without mounting a second router.
//
// This is deliberately a *small*, explicit compatibility matcher rather than
// a general route language.  The frozen legacy inventory uses only absolute
// exact paths and terminal /* splats.  Any other pattern refuses loudly so a
// future route cannot silently gain semantics this bridge does not preserve.
// Exact routes outrank splats; among splats, the deeper base outranks its
// parent.  Those are the only ranking rules current entries need, including
// /dailys/tasks over /dailys/*.
//
// AppRoute is a marker.  AppRoutes consumes its direct children and returns
// the selected element; it does not consult React Router context or browser
// history.  The caller supplies the routed location exactly as it did before.
// ============================================================================

import React from 'react';
import type {ReactNode} from 'react';

import {routePatternBase, routePatternOwns} from './viewRoutes.js';

export type AppRoutesLocation = string | {pathname?: unknown} | null | undefined;

export type AppRouteProps = {
  path: string;
  element: ReactNode;
};

export type AppRoutesProps = {
  location: AppRoutesLocation;
  children?: ReactNode;
};

export type LegacyRouteDefinition<T> = {
  path: string;
  element: T;
};

type RankedRoute<T> = LegacyRouteDefinition<T> & {index: number; score: number};

function pathnameFromLocation(location: AppRoutesLocation): string | null {
  if (typeof location === 'string') return location || null;
  if (!location || typeof location !== 'object') return null;
  return typeof location.pathname === 'string' && location.pathname ? location.pathname : null;
}

function assertLegacyPattern(pattern: unknown): asserts pattern is string {
  if (typeof pattern !== 'string' || !pattern.startsWith('/')) {
    throw new TypeError('AppRoute paths must be absolute strings');
  }
  const star = pattern.indexOf('*');
  if (star !== -1 && !pattern.endsWith('/*')) {
    throw new TypeError(`AppRoute path ${pattern} may use only a terminal /* splat`);
  }
  if (star !== -1 && star !== pattern.length - 1) {
    throw new TypeError(`AppRoute path ${pattern} may use only one terminal /* splat`);
  }
  if (pattern.includes(':') || pattern.includes('?') || pattern.includes('#')) {
    throw new TypeError(`AppRoute path ${pattern} is outside the legacy exact-or-splat contract`);
  }
}

function scoreLegacyPattern(pattern: string): number {
  const base = routePatternBase(pattern);
  const segments = base.split('/').filter(Boolean).length;
  // Exact wins a tie with its matching splat.  Segment depth lets /a/b/* win
  // over /a/*, matching the specificity the legacy route inventory relies on.
  return segments * 10 + (pattern.endsWith('/*') ? 0 : 1);
}

/**
 * Pure legacy route selection.  It is exported so the bridge has an executable
 * parity contract independent of either browser-history router.
 */
export function selectLegacyRoute<T>(
  location: AppRoutesLocation,
  routes: readonly LegacyRouteDefinition<T>[],
): T | null {
  const pathname = pathnameFromLocation(location);
  if (!pathname) return null;

  let selected: RankedRoute<T> | null = null;
  for (const [index, route] of routes.entries()) {
    assertLegacyPattern(route.path);
    if (!routePatternOwns(route.path, pathname)) continue;
    const candidate: RankedRoute<T> = {...route, index, score: scoreLegacyPattern(route.path)};
    if (
      !selected ||
      candidate.score > selected.score ||
      (candidate.score === selected.score && candidate.index < selected.index)
    ) {
      selected = candidate;
    }
  }
  return selected ? selected.element : null;
}

/** Marker consumed by AppRoutes. Rendering it directly is an ownership bug. */
export function AppRoute(_props: AppRouteProps): null {
  throw new Error('AppRoute must be a direct child of AppRoutes');
}

/**
 * Router-neutral replacement for React Router's legacy <Routes> use here.
 * Direct AppRoute children keep the existing source-level route inventory
 * intact while their matching is independent of a router provider.
 */
export function AppRoutes({location, children}: AppRoutesProps): ReactNode {
  const routes: LegacyRouteDefinition<ReactNode>[] = [];
  React.Children.forEach(children, (child) => {
    if (child === null || child === undefined || typeof child === 'boolean') return;
    if (!React.isValidElement<AppRouteProps>(child) || child.type !== AppRoute) {
      throw new TypeError('AppRoutes accepts only direct AppRoute children');
    }
    routes.push({path: child.props.path, element: child.props.element});
  });
  return selectLegacyRoute(location, routes);
}
